/**
 * Global theme management — Light / Dark / System.
 *
 * The preference lives in three places, reconciled in this order:
 *   1. localStorage ('emhub_theme') — applied synchronously before first paint
 *      so there's no light flash on reload.
 *   2. The server (users.theme) — source of truth across devices; synced after
 *      login via syncFromServer() (Header calls it on mount).
 *   3. The OS — only consulted when the preference is 'system'.
 *
 * The resolved theme is written to <html data-theme="..."> which drives the
 * CSS variables in src/styles/theme.css and dashboard.css.
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../api/api';

const STORAGE_KEY = 'emhub_theme';
const THEMES = ['light', 'dark', 'system'];

const auth = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : null;
};

const readStored = () => {
  const v = localStorage.getItem(STORAGE_KEY);
  return THEMES.includes(v) ? v : 'system';
};

const systemDark = () =>
  window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolve = (pref) => (pref === 'system' ? (systemDark() ? 'dark' : 'light') : pref);

// Pages that must never change theme (e.g. the login page is always light)
// set a forced theme for their lifetime; every apply() path respects it.
let forcedTheme = null;

const apply = (pref) => {
  document.documentElement.setAttribute('data-theme', forcedTheme || resolve(pref));
};

export const setForcedTheme = (t) => {
  forcedTheme = THEMES.includes(t) ? t : null;
  apply(readStored());
};

// Apply immediately at module load — before React renders — to avoid a flash.
apply(readStored());

const ThemeContext = createContext({
  theme: 'system',
  resolved: 'light',
  setTheme: () => {},
  syncFromServer: () => {},
});

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStored);
  const synced = useRef(false);

  // Keep <html data-theme> in step with the preference.
  useEffect(() => {
    apply(theme);
    if (theme !== 'system' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (!THEMES.includes(next)) return;
    setThemeState(next);
    localStorage.setItem(STORAGE_KEY, next);
    const a = auth();
    if (a) {
      axios.put(`${API_URL}/api/users/me/theme`, { theme: next }, a).catch(() => {});
    }
  }, []);

  // Pull the saved preference once per app load (Header calls this after login).
  const syncFromServer = useCallback(() => {
    if (synced.current) return;
    const a = auth();
    if (!a) return;
    synced.current = true;
    axios
      .get(`${API_URL}/api/users/me/theme`, a)
      .then((r) => {
        const t = r.data?.theme;
        if (THEMES.includes(t) && t !== readStored()) {
          setThemeState(t);
          localStorage.setItem(STORAGE_KEY, t);
        }
      })
      .catch(() => { synced.current = false; });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolved: resolve(theme), setTheme, syncFromServer }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
